'use client';

import { useAuthentication } from '@/hooks/useAuthentication';
import { getListStaff } from '@/services/global/admin.service';
import { useCallback, useEffect, useMemo, useState } from 'react';

export type StaffStatus =
  | 'pending_invite'
  | 'invited_pending_signup'
  | 'registered_pending_onboarding'
  | 'registered_onboarded';

export type Staff = {
  surname: string | null;
  forename: string | null;
  email: string;
  status: StaffStatus;
};

export type GetListStaffResult = {
  size: number;
  staff: Staff[];
};

type UseStaffListOptions = {
  schoolId?: string;
  pageSize?: number;
};

export function useStaffList({ pageSize = 10 }: UseStaffListOptions) {
  const [raw, setRaw] = useState<Staff[]>([]);
  const [size, setSize] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const { schoolContext } = useAuthentication();

  const fetchData = useCallback(async () => {
    if (!schoolContext?.documentId) {
      setRaw([]);
      setSize(0);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const { data } = await getListStaff(schoolContext?.documentId);
      setRaw(data?.staff ?? []);
      setSize(data?.size ?? data?.staff?.length ?? 0);

      setCurrentPage((prev) => {
        const pages = Math.max(1, Math.ceil((data?.staff?.length ?? 0) / pageSize));
        return Math.min(prev, pages);
      });
    } finally {
      setLoading(false);
    }
  }, [schoolContext?.documentId, pageSize]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(raw.length / pageSize)), [raw.length, pageSize]);

  const staff = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return raw.slice(start, start + pageSize);
  }, [raw, currentPage, pageSize]);

  const handlePageChange = useCallback(
    (page: number) => {
      const next = Math.min(Math.max(page, 1), totalPages);
      if (next !== currentPage) setCurrentPage(next);
    },
    [currentPage, totalPages],
  );

  const refetch = useCallback(() => {
    fetchData();
  }, [fetchData]);

  return {
    staff,
    size,
    currentPage,
    totalPages,
    handlePageChange,
    loading,
    error,
    refetch,
  };
}
